"use dom"

import { useEffect, useRef } from "react"
import { Chart, LineController, LineElement, PointElement, LinearScale, CategoryScale, Legend, Tooltip } from "chart.js"

// Register Chart.js components
Chart.register(LineController, LineElement, PointElement, LinearScale, CategoryScale, Legend, Tooltip)

// Color palette for up to 10 products
const COLORS = [
  "#3B82F6", // blue
  "#10B981", // emerald
  "#F59E0B", // amber
  "#EF4444", // red
  "#8B5CF6", // violet
  "#EC4899", // pink
  "#06B6D4", // cyan
  "#84CC16", // lime
  "#F97316", // orange
  "#6366F1", // indigo
]

type ProductData = {
  productId: number
  name: string
  data: { period: string; count: number }[]
}

type Props = {
  products: ProductData[]
  dom?: import("expo/dom").DOMProps
}

export default function PurchaseLineChart({ products }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const chartRef = useRef<Chart | null>(null)

  useEffect(() => {
    if (!canvasRef.current || products.length === 0) return

    // Destroy previous chart instance
    if (chartRef.current) {
      chartRef.current.destroy()
    }

    // Build unique sorted labels (x-axis) from all products' periods
    const allPeriods = new Set<string>()
    for (const product of products) {
      for (const d of product.data) {
        allPeriods.add(d.period)
      }
    }
    const labels = Array.from(allPeriods).sort()

    // If no periods found (no data), show placeholder message
    if (labels.length === 0) {
      return
    }

    // Build one dataset per product (each gets its own line)
    const datasets = products.map((product, index) => {
      // Create a map of period -> count for this product
      const periodToCount = new Map<string, number>()
      for (const d of product.data) {
        periodToCount.set(d.period, d.count)
      }

      // Map labels to this product's counts (0 if no data for that period)
      const dataPoints = labels.map((label) => periodToCount.get(label) ?? 0)
      const color = COLORS[index % COLORS.length]

      return {
        label: product.name,
        data: dataPoints,
        borderColor: color,
        backgroundColor: color,
        tension: 0.3,
        fill: false,
        pointRadius: 4,
        pointHoverRadius: 6,
        borderWidth: 2,
      }
    })

    // Create chart
    const ctx = canvasRef.current.getContext("2d")
    if (!ctx) return

    chartRef.current = new Chart(ctx, {
      type: "line",
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: "bottom",
            labels: {
              boxWidth: 12,
              padding: 12,
              font: { size: 11 },
            },
          },
          tooltip: {
            mode: "index",
            intersect: false,
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { font: { size: 10 }, maxRotation: 45 },
          },
          y: {
            beginAtZero: true,
            grid: { color: "#f0f0f0" },
            ticks: { font: { size: 10 }, stepSize: 1 },
          },
        },
        interaction: {
          mode: "nearest",
          axis: "x",
          intersect: false,
        },
      },
    })

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy()
        chartRef.current = null
      }
    }
  }, [products])

  if (products.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          color: "#9ca3af",
          fontSize: 14,
        }}
      >
        لا توجد بيانات
      </div>
    )
  }

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <canvas ref={canvasRef} />
    </div>
  )
}
